import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  CircleX,
  Eye,
  LoaderCircle,
  Send,
  CheckCheck,
} from "lucide-react";
import type {
  MessageStatusName,
  ReportMessageListItem,
  ReportMessageListResponse,
  ReportMessagePageSize,
} from "@multi-chat/shared";
import { REPORT_MESSAGE_PAGE_SIZES } from "@multi-chat/shared";
import { ReportResultState } from "./ReportResultState.js";
import type { ReportEmptyKind } from "./report-empty-kind.js";
import { ChannelIdentityBadge } from "../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../components/channel/channel-presentation.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import type { OutgoingReportColumnId } from "./report-column-defs.js";
import { reportColumnHeaderDragHandlers } from "./report-column-header-drag.js";
import { ReportColumnResizeHandle } from "./ReportColumnResizeHandle.js";
import { ReportPaginationBar } from "./ReportPaginationBar.js";
import {
  reportColumnStyle,
  reportTableBodyCellClass,
  reportTableBodyRowClass,
  reportTableClass,
  reportTableHeaderCellClass,
  reportTableHeadRowClass,
  reportTableScrollClass,
  reportTableWorkspaceClass,
  contactsTableHeaderLabelClass,
} from "./report-table-presentation.js";
import { localizeDeliveryFailureReason } from "../whatsapp/whatsapp-delivery-failure-presentation.js";
import {
  formatReportDateTime,
  messagePreviewOrTemplate,
  originBadgeLabel,
  outgoingReportStatusChipClass,
  recipientDisplayPrimary,
  recipientDisplaySecondary,
  showingRangeBounds,
  statusLabelKey,
} from "./outgoing-report-presentation.js";
import { ReportOriginChip } from "./ReportOriginChip.js";

function StatusIcon({ status }: { status: MessageStatusName }): React.JSX.Element {
  switch (status) {
    case "PENDING":
    case "SENDING":
    case "RECEIVED":
      return (
        <LoaderCircle
          size={12}
          className="animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      );
    case "SENT":
      return <Send size={12} aria-hidden="true" />;
    case "DELIVERED":
      return <CheckCheck size={12} aria-hidden="true" />;
    case "READ":
      return <Eye size={12} aria-hidden="true" />;
    case "FAILED":
      return <CircleX size={12} aria-hidden="true" />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function OutgoingReportTable({
  list,
  loading,
  page,
  pageSize,
  emptyKind,
  visibleColumns,
  widths,
  onResizeColumn,
  onReorderColumn,
  onPageChange,
  onPageSizeChange,
  onResetFilters,
  onOpenMessage,
}: {
  list: ReportMessageListResponse | null;
  loading: boolean;
  page: number;
  pageSize: number;
  emptyKind: ReportEmptyKind;
  visibleColumns: readonly OutgoingReportColumnId[];
  widths: Readonly<Record<OutgoingReportColumnId, number>>;
  onResizeColumn: (id: OutgoingReportColumnId, width: number) => void;
  onReorderColumn: (fromId: OutgoingReportColumnId, toId: OutgoingReportColumnId) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: ReportMessagePageSize) => void;
  onResetFilters: () => void;
  onOpenMessage?: (messageId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const items = list?.items ?? [];
  const total = list?.totalCount ?? 0;
  const totalPages = Math.max(list?.totalPages ?? 1, 1);
  const range = showingRangeBounds({ page, pageSize, count: items.length });
  const colCount = Math.max(visibleColumns.length, 1);
  const dragFromRef = useRef<OutgoingReportColumnId | null>(null);
  const resizeRef = useRef<{
    id: OutgoingReportColumnId;
    startX: number;
    startWidth: number;
    rtl: boolean;
  } | null>(null);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const active = resizeRef.current;
      if (active == null) return;
      const delta = active.rtl ? active.startX - event.clientX : event.clientX - active.startX;
      onResizeColumn(active.id, active.startWidth + delta);
    };
    const onUp = () => {
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onResizeColumn]);

  const labelFor = (id: OutgoingReportColumnId) => t(`reports.outgoing.columns.${id}`);

  return (
    <div className={reportTableWorkspaceClass} data-testid="reports-outgoing-table">
      <div className={reportTableScrollClass}>
        <table className={reportTableClass} style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <thead>
            <tr className={reportTableHeadRowClass}>
              {visibleColumns.map((columnId) => (
                <th
                  key={columnId}
                  className={reportTableHeaderCellClass}
                  style={reportColumnStyle(widths[columnId])}
                  {...reportColumnHeaderDragHandlers(columnId, dragFromRef, onReorderColumn)}
                  data-testid={`reports-outgoing-column-${columnId}`}
                >
                  <span className={contactsTableHeaderLabelClass}>{labelFor(columnId)}</span>
                  <ReportColumnResizeHandle
                    label={t("reports.chrome.resizeColumn", { column: labelFor(columnId) })}
                    onResizeStart={(clientX) => {
                      const rtl =
                        typeof document !== "undefined" &&
                        getComputedStyle(document.documentElement).direction === "rtl";
                      resizeRef.current = {
                        id: columnId,
                        startX: clientX,
                        startWidth: widths[columnId],
                        rtl,
                      };
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td className={reportTableBodyCellClass} colSpan={colCount}>
                  <div className="grid gap-2 py-3" aria-busy="true">
                    <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                    <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                    <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                  </div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className={reportTableBodyCellClass} colSpan={colCount}>
                  <ReportResultState
                    kind={emptyKind}
                    i18nPrefix="reports.outgoing"
                    onResetFilters={onResetFilters}
                    testId="reports-outgoing-empty"
                  />
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <ReportRow
                  key={row.messageId}
                  row={row}
                  visibleColumns={visibleColumns}
                  widths={widths}
                  onOpenMessage={onOpenMessage}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <ReportPaginationBar
        page={page}
        pageSize={pageSize}
        totalCount={total}
        totalPages={totalPages}
        pageSizes={REPORT_MESSAGE_PAGE_SIZES}
        onPageChange={onPageChange}
        onPageSizeChange={(size) => onPageSizeChange(size as ReportMessagePageSize)}
        showingLabel={t("reports.outgoing.showingRange")}
        pageOfLabel={t("reports.outgoing.pageOf")}
        prevLabel={t("reports.outgoing.prev")}
        nextLabel={t("reports.outgoing.next")}
        pageSizeLabel={t("reports.outgoing.pageSize")}
        testIdPrefix="reports-outgoing"
        rangeStart={range.start}
        rangeEnd={range.end}
      />
    </div>
  );
}

function ReportRow({
  row,
  visibleColumns,
  widths,
  onOpenMessage,
}: {
  row: ReportMessageListItem;
  visibleColumns: readonly OutgoingReportColumnId[];
  widths: Readonly<Record<OutgoingReportColumnId, number>>;
  onOpenMessage?: (messageId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const primary = recipientDisplayPrimary(row.contact);
  const secondary = recipientDisplaySecondary(row.contact);
  const sender =
    row.endpoint.displayLabel?.trim() || row.endpoint.externalEndpointId || "—";
  const interactive = onOpenMessage != null;

  const renderCell = (columnId: OutgoingReportColumnId): React.ReactNode => {
    switch (columnId) {
      case "date":
        return (
          <LtrValue className="tabular-nums text-[0.75rem]">
            {formatReportDateTime(row.createdAt, i18n.language)}
          </LtrValue>
        );
      case "recipient":
        return (
          <div className="grid min-w-0 max-w-full gap-0.5 overflow-hidden">
            <span className="truncate font-medium [unicode-bidi:plaintext]" dir="auto">
              {primary}
            </span>
            {secondary ? (
              <LtrValue className="max-w-full truncate text-[0.6875rem] text-muted" title={secondary}>
                {secondary}
              </LtrValue>
            ) : null}
          </div>
        );
      case "platform":
        return (
          <ChannelIdentityBadge
            platformType={row.platform}
            size="table"
            label={t(`channel.${channelLabelI18nKey(row.platform)}`)}
          />
        );
      case "sender":
        return (
          <span className="truncate text-[0.75rem]" title={sender}>
            {sender}
          </span>
        );
      case "origin":
        return (
          <span data-testid="reports-outgoing-origin-badge">
            <ReportOriginChip origin={row.origin} label={originBadgeLabel(t, row.origin)} />
          </span>
        );
      case "campaign":
        return (
          <span className="truncate text-[0.75rem]">
            {row.campaign?.campaignName?.trim() || "—"}
          </span>
        );
      case "message":
        return (
          <span className="line-clamp-2 text-[0.75rem] text-muted">
            {messagePreviewOrTemplate(row)}
          </span>
        );
      case "status":
        return (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium",
              outgoingReportStatusChipClass(row.status),
            )}
            data-testid="reports-outgoing-status-badge"
            title={
              row.failureReason
                ? (localizeDeliveryFailureReason(t, row.failureReason) ?? row.failureReason)
                : undefined
            }
          >
            <StatusIcon status={row.status} />
            {t(statusLabelKey(row.status))}
          </span>
        );
      default: {
        const _exhaustive: never = columnId;
        return _exhaustive;
      }
    }
  };

  return (
    <tr
      className={cn(reportTableBodyRowClass)}
      data-clickable={interactive ? "true" : undefined}
      data-testid="reports-outgoing-row"
      data-message-id={row.messageId}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onOpenMessage(row.messageId) : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenMessage(row.messageId);
              }
            }
          : undefined
      }
    >
      {visibleColumns.map((columnId) => (
        <td
          key={columnId}
          className={reportTableBodyCellClass}
          style={reportColumnStyle(widths[columnId])}
        >
          {renderCell(columnId)}
        </td>
      ))}
    </tr>
  );
}
